import { ControllerProps } from "csi/middleware/common";
import Checkbox from "designSystem/components/checkbox/Checkbox";
import { ModalDialogProps } from "designSystem/components/dialog/ModalDialog";
import PopupDialog from "designSystem/components/dialog/PopupDialog";
import RadioGroup, { RadioOption } from "designSystem/components/radio/RadioGroup";
import {
	Box,
	Button,
	FlexColumn,
	FlexRowCenter,
	FormControlLabel,
	GridRow,
	OverflowText
} from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { black } from "designSystem/styles/color";
import { disabledCursorStyling } from "designSystem/styles/style-functions";
import { ButtonEnum } from "designSystem/types/button-types";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { useRecipeLabelStore } from "./RecipeLabelStore";

/**
 * Return radio checkbox option with a checkbox as a nested labelContent
 * @param props properties of the option
 * @returns radio checkbox option with a checkbox as a nested labelContent or the same props
 */
function RadioCheckboxOption (props): RadioOption<string> {
	var copy= { ...props };
	if (copy.checkboxProps) {
		copy.labelContent = (
			<FormControlLabel
				className="csi-outer-radio-label"
				control={(
					<Checkbox {...copy.checkboxProps} />
				)}
				sx={{
					alignItems: "start",
					cursor: disabledCursorStyling(copy.disabled),
				}}
				value={copy.value}
			/>
		);
		return copy;
	}
	return props;
};

export interface RecipeOperationDialogProps extends ModalDialogProps, ControllerProps {
}

const RecipeOperationDialog: FunctionComponent<RecipeOperationDialogProps> = observer((props) => {
	const loc = useLocale();
	const store = useRecipeLabelStore();
	const {
		buildRequest,
		isRunDisabled,
		setFormSubmitted,
	} = store;

	const { actionController, children, onClose, onSave, ...otherProps } = props;

	// Send request to update recipe label
	const executeUpdateRecipeLabelOperation = async () => {
		// Disable run button so user cannot 'Run' more than once
		setFormSubmitted(true);
		onSave(buildRequest()
			.post());
		return true;
	};

	function getIngredientCheckboxOption(props): RadioOption<string>[] {
		return props.map(RadioCheckboxOption);
	}

	return (
		<PopupDialog
			onClose={onClose}
			showClose
			{...otherProps}
		>
			{{
				main: (
					<Box sx={{ width: 550 }}>
						<FlexColumn px={3}>
							<FlexColumn
								alignItems="left"
								width="100%"
							>
								<FlexRowCenter>
									<RadioGroup
										name={loc.resource("CalculateIngredients")}
										onChange= {(event, val:string)=>{
											store.updateRecipeLabelAction("Ingredients", val, false, 0);
										}}
										options={getIngredientCheckboxOption(store.ingredientOptions)}
										sx={{
											"div span:first-of-type:not(label span:last-of-type)": store.isIngredientSectionDisabled ? { fontWeight: 600 } : {
												color: black[100],
												fontWeight: 600,
											},
										}}
										value={store.ingredientChecked}
									/>
								</FlexRowCenter>
								<FlexRowCenter pt={6}>
									<RadioGroup
										name={loc.resource("CalculateAllergens")}
										onChange= {(event, val)=>{
											store.updateRecipeLabelAction("Allergens", val);
										}}
										options={store.allergenOptions}
										sx={{
											"& .MuiTypography-body1:not(:last-child)": store.isAllergenSectionDisabled ? { fontWeight: 600 }  : {
												color: black[100],
												fontWeight: 600,
											},
										}}
										value={store.allergenChecked}
									/>
										
								</FlexRowCenter>
								<FlexRowCenter pt={6}>
									<RadioGroup										
										name={loc.resource("CalculateNutrients")}
										onChange= {(event, val)=>{
											store.updateRecipeLabelAction("Nutrients", val);
										}}
										options={store.nutrientOptions}
										sx={{
											"& .MuiTypography-body1:not(:last-child)": store.isNutrientSectionDisabled ? { fontWeight: 600 } : {
												color: black[100],
												fontWeight: 600,
											},
										}}
										value={store.nutrientChecked}
									/>
								</FlexRowCenter>
							</FlexColumn>
						</FlexColumn>
					</Box>
				),
				buttons: (
					<>
						<GridRow
							alignItems="center"
							flexWrap="nowrap"
							growChild={0}
							spacing={2}
							textAlign="left"
						>
							<OverflowText /> 
							<Button
								design="primary"
								disabled={isRunDisabled}
								onClick={executeUpdateRecipeLabelOperation}
								res={ButtonEnum.BUTTON_RUN}
							/>
							<Button
								design="link"
								onClick={onClose}
								res={ButtonEnum.BUTTON_CANCEL}
							/>
						</GridRow>
					</>
				),
			}}
		</PopupDialog>
	);
});

export default RecipeOperationDialog;

